/* @flow */
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { debounce } from 'lodash';

import { initializeTable, loadMemberships } from 'reducers/peopleTables';
import Group from 'entities/Group';
import GroupMembership from 'entities/GroupMembership';
import PeopleTableBody from 'components/PeopleTableBody';
import type { ClassMembershipFetchOptions } from 'clients/api/classMemberSearch';
import type { Filters } from 'reducers/tables';

const PER_PAGE = 20;

type Props = {
  sort: string,
  group: Group,
  query: string,
  filters: Filters,
  adminConsoleAccess: ?boolean,
  canViewFamily: boolean,

  /* redux props */
  page?: number,
  allLoaded?: boolean,
  error?: Error,
  loading?: boolean,
  initialized: boolean,
  memberships?: GroupMembership[],
  initializeTable: (groupId: Backbone$Id, groupUuid: ?string) => any,
  loadMemberships: (groupId: Backbone$Id, options: ClassMembershipFetchOptions) => any,
};

class PeopleTableBodyContainer extends React.Component<Props> {
  componentDidMount() {
    if (!this.props.initialized) {
      this.props.initializeTable(this.props.group.id, this.props.group.getUuid());
    }

    if (this.props.error) {
      this.loadPage(1);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const initialized = !prevProps.initialized && this.props.initialized;
    const newFilters = prevProps.filters !== this.props.filters;
    const newQuery = prevProps.query !== this.props.query;
    const newSort = prevProps.sort !== this.props.sort;
    const needToReinitialize = prevProps.initialized && !this.props.initialized;

    if (needToReinitialize) {
      this.reinitializeTable(this.props.group.id, this.props.group.getUuid());
    }

    if (initialized || newSort || newQuery || newFilters) {
      this.loadPage(1, this.props);
    }
  }

  reinitializeTable = debounce((groupId: Backbone$Id, groupUuid: string) => {
    this.props.initializeTable(groupId, groupUuid);
  });

  loadPage(page: number, nextProps?: Props) {
    const props = nextProps || this.props;

    let sortOptions = {};

    if (props.sort) {
      switch (props.sort) {
        case 'first_name_asc':
          sortOptions = { sort: 'first_name', direction: 'asc' };
          break;
        case 'first_name_desc':
          sortOptions = { sort: 'first_name', direction: 'desc' };
          break;
        case 'last_name_asc':
          sortOptions = { sort: 'last_name', direction: 'asc' };
          break;
        case 'last_name_desc':
          sortOptions = { sort: 'last_name', direction: 'desc' };
          break;
        case 'join_date_asc':
          sortOptions = { sort: 'join_date', direction: 'asc' };
          break;
        case 'join_date_desc':
          sortOptions = { sort: 'join_date', direction: 'desc' };
          break;
        case 'role_asc':
          sortOptions = { sort: 'role', direction: 'asc' };
          break;
        case 'role_desc':
          sortOptions = { sort: 'role', direction: 'desc' };
          break;
        default:
      }
    }

    const filterKeys = props.filters.keySeq().toArray();

    if (this.props.group.isRoot()) {
      // we no longer support/render the people list on the root group,
      // so we should just log an error and return an empty array
      console.error('PeopleTableBodyContainer - tried to render for All Classes');
      return null;
    } else {
      return this.props.loadMemberships(this.props.group.id, {
        page,
        perPage: PER_PAGE,
        query: props.query,
        roles: filterKeys,
        sort: sortOptions.sort,
        sortDirection: sortOptions.direction,
      });
    }
  }

  loadNextPage = () => {
    if (this.props.loading) {
      return null;
    } else {
      return this.loadPage(this.props.page + 1);
    }
  };

  getFamilyMemberSummaries = () => {
    const { group, memberships } = this.props;

    if (group.isRoot()) {
      return [];
    }

    if (!memberships) {
      return [];
    }

    const members: Array<*> = memberships.map(member => {
      return member.toJSON();
    });

    return members;
  };

  render() {
    const { canViewFamily } = this.props;

    if (!this.props.initialized) {
      return <tbody />;
    }

    return (
      <PeopleTableBody
        group={this.props.group}
        error={this.props.error}
        loadNextPage={this.loadNextPage}
        allLoaded={this.props.allLoaded}
        memberships={this.props.memberships}
        adminConsoleAccess={this.props.adminConsoleAccess}
        canViewFamily={canViewFamily}
        memberSummaries={canViewFamily ? this.getFamilyMemberSummaries() : []}
      />
    );
  }
}

function mapStateToProps(state: any, props: Props) {
  const table = state.peopleTables.get(props.group.id);

  if (!table) {
    return { initialized: false };
  }

  return {
    initialized: true,
    page: table.page,
    error: table.error,
    loading: table.loading,
    allLoaded: table.allLoaded,
    memberships: table.memberships,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ initializeTable, loadMemberships }, dispatch);
}

export { PeopleTableBodyContainer };
export default connect(mapStateToProps, mapDispatchToProps)(PeopleTableBodyContainer);
